import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarCheck, CheckCircle2, Clock, Flame, Snowflake, Sparkles, Wind, Wrench } from "lucide-react";
import { toast } from "sonner";

const SERVICES = [
  { id: "ac", label: "AC Repair", icon: Snowflake },
  { id: "heat", label: "Heating", icon: Flame },
  { id: "maint", label: "Tune-Up", icon: CheckCircle2 },
  { id: "iaq", label: "Air Quality", icon: Sparkles },
  { id: "other", label: "Other", icon: Wrench },
  { id: "estimate", label: "Free Estimate", icon: Wind },
];

const SLOTS = ["Morning 7–10A", "Midday 10A–1P", "Afternoon 1–5P"];

export default function BookingSection() {
  const [service, setService] = useState(0);
  const [day, setDay] = useState(0);
  const [slot, setSlot] = useState(0);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);

  const isOther = SERVICES[service].id === "other";

  const days = useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 7; i++) {
      const d = new Date(now);
      d.setDate(now.getDate() + i);
      out.push({
        weekday: d.toLocaleDateString("en-US", { weekday: "short" }),
        dayNum: d.getDate(),
        full: d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }),
      });
    }
    return out;
  }, []);

  const submit = async () => {
    if (isOther && notes.trim().length < 5) {
      toast.error("Tell us briefly what's going on");
      return;
    }
    if (name.trim().length < 2) {
      toast.error("Please enter your name");
      return;
    }
    if (phone.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service: SERVICES[service].id,
          day: days[day].full,
          slot: SLOTS[slot],
          name: name.trim(),
          phone,
          notes: isOther ? notes.trim() : "",
        }),
      });
      if (!res.ok) throw new Error("send failed");
      toast.success("Appointment requested", {
        description: `${SERVICES[service].label} · ${days[day].full}, ${SLOTS[slot]} · We'll call ${name.trim()} to confirm.`,
      });
      setName("");
      setPhone("");
      setNotes("");
    } catch {
      toast.error("Couldn't send the request", {
        description: "Please call 615-593-2628 and we'll book you on the spot.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-section" className="relative py-24 sm:py-32">
      <div className="pointer-events-none absolute inset-x-0 top-24 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 34 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.26em] text-forge">
              <CalendarCheck className="h-3.5 w-3.5" /> 06 — Book a Visit
            </span>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
              Lock in your appointment.
            </h2>
            <p className="mt-4 max-w-md text-base text-slate-400 sm:text-lg">
              Pick a day and a time window — we&apos;ll call to confirm before
              anyone rolls. Open Sun–Sat, 7A–5P.
            </p>
            <div className="mt-8 flex items-center gap-3 rounded-2xl border border-ice/20 bg-ice/[0.06] px-5 py-4">
              <Clock className="h-5 w-5 shrink-0 text-ice" />
              <p className="text-sm text-slate-300">
                Prefer to talk? Call{" "}
                <a href="tel:+16155932628" className="font-bold text-ice transition-colors hover:text-forge">
                  615-593-2628
                </a>{" "}
                and we&apos;ll book you on the spot.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 34 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-3xl border border-white/[0.08] bg-navy-800/70 p-7 sm:p-9 lg:col-span-7"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Service</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {SERVICES.map((s, i) => {
                const Icon = s.icon;
                const on = service === i;
                return (
                  <button
                    key={s.id}
                    data-testid={`booking-service-${s.id}`}
                    onClick={() => setService(i)}
                    className={`flex items-center gap-2 rounded-full border px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${
                      on
                        ? "border-forge/60 bg-forge/15 text-slate-50"
                        : "border-white/10 bg-white/[0.03] text-slate-400 hover:border-white/25 hover:text-slate-200"
                    }`}
                  >
                    <Icon className={`h-3.5 w-3.5 ${on ? "text-forge" : ""}`} />
                    {s.label}
                  </button>
                );
              })}
            </div>

            <AnimatePresence initial={false}>
              {isOther && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <textarea
                    data-testid="booking-notes-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value.slice(0, 500))}
                    placeholder="Tell us what's going on — strange noise, no heat, new install, anything."
                    rows={3}
                    className="mt-4 w-full resize-none rounded-xl border border-forge/40 bg-forge/[0.06] px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-forge/60 focus:outline-none"
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <p className="mt-7 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Day</p>
            <div className="mt-3 grid grid-cols-7 gap-1.5">
              {days.map((d, i) => (
                <button
                  key={d.full}
                  data-testid={`booking-day-${i}`}
                  onClick={() => setDay(i)}
                  className={`rounded-xl border py-3 text-center transition-colors duration-200 ${
                    day === i
                      ? "border-forge/60 bg-forge/15 text-slate-50"
                      : "border-white/10 bg-white/[0.03] text-slate-400 hover:border-white/25"
                  }`}
                >
                  <span className="block font-mono text-[9px] uppercase tracking-wider">{d.weekday}</span>
                  <span className="mt-1 block font-display text-base font-bold">{d.dayNum}</span>
                </button>
              ))}
            </div>

            <p className="mt-7 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Time Window</p>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
              {SLOTS.map((s, i) => (
                <button
                  key={s}
                  data-testid={`booking-slot-${i}`}
                  onClick={() => setSlot(i)}
                  className={`rounded-xl border px-4 py-3.5 text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${
                    slot === i
                      ? "border-ice/60 bg-ice/15 text-slate-50"
                      : "border-white/10 bg-white/[0.03] text-slate-400 hover:border-white/25"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              <input
                data-testid="booking-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-forge/50 focus:outline-none"
              />
              <input
                data-testid="booking-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone number"
                inputMode="tel"
                className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-forge/50 focus:outline-none"
              />
            </div>

            <button
              data-testid="booking-submit-button"
              onClick={submit}
              disabled={sending}
              className="mt-6 flex w-full items-center justify-center gap-2.5 rounded-full bg-gradient-to-br from-forge to-forge-hi py-4 font-display text-sm font-bold uppercase tracking-wider text-white shadow-[0_10px_36px_rgba(255,107,0,0.35)] transition-transform duration-200 hover:scale-[1.02] active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <CalendarCheck className="h-4 w-4" />
              {sending ? "Sending..." : "Request Appointment"}
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
